import type { FC } from "react";
import {
  Box,
  Button,
  Typography,
  makeStyles,
  Grid,
  Card,
  CardContent,
  Hidden,
  useTheme,
  useMediaQuery,
  Theme
} from "@material-ui/core";
import { Quote } from "../../../api/Quote/Quote";
import React from "react";
import { QuoteHr } from "../../quotePage/components/QuoteHr";
import { colors } from "swyft-react-core";
import { hasSubscriptions, paymentTypeTitle } from "../../../app/utils";
import { getFirstImage, ProductSubscription } from "../../../api/Product/ProductSubscriptionApi";
import QuoteButtonAddSubscription from "../../quotePage/components/QuoteButtonAddSubscription";
import { history } from "../../../app/rootReducer";
import { PriceFormat } from "../../../components/typography/PriceFormat";

interface Props {
  quote: Quote;
  subscriptions: ProductSubscription[] | null;
  onAddSubscription: (subscription: ProductSubscription) => void;
}

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    '& [class*="Button-contained.Mui-disabled"]': {
      backgroundColor: colors.white,
      border: "1px dashed black",
      fontWeight: "bold",
      color: colors.darkGrey
    }
  },
  box: {
    boxShadow: "0px 3px 6px 0px rgba(0, 0, 0, 0.16)"
  },
  image: {
    margin: "0 auto",
    display: "block",
    width: "100%",
    maxWidth: "15rem"
  },
  icon: {
    verticalAlign: "text-top"
  }
}));

const QuoteAddonWizardSubscriptions: FC<Props> = ({ quote, subscriptions, onAddSubscription }) => {
  const classes = useStyles();
  const theme = useTheme();
  const isSmall = useMediaQuery(theme.breakpoints.down("md"));

  if (!hasSubscriptions(subscriptions)) {
    return null;
  }

  const subscriptionView = function (subscription: ProductSubscription) {
    if (!subscription) {
      return null;
    }

    const onView = function () {
      history.push("/protect");
    };

    const highlightSection = () => {
      if (subscription.highlights === null || subscription.highlights?.length === 0) {
        return null;
      }

      return (
        <Grid container spacing={1}>
          {subscription.highlights?.map((i, index) => (
            <Grid item md={6} key={subscription.name + "wizard-highlights" + index}>
              {i}
            </Grid>
          ))}
        </Grid>
      );
    };

    return (
      <Box mb={isSmall ? 1 : 3} key={subscription.name + "wizard-subscription"} className={classes.box}>
        <Card square>
          <CardContent>
            <Grid container alignItems="center" spacing={3}>
              <Grid item md={4} xs={12}>
                <Box>
                  <img className={classes.image} alt={subscription.name} src={getFirstImage(subscription)?.url} />
                </Box>
              </Grid>
              <Grid item md={8} xs={12}>
                <Box textAlign="center">
                  <Typography variant="h2">{subscription.name}</Typography>
                  <Typography variant="body1">{subscription.highLevelDescription}</Typography>
                  <Hidden mdDown>
                    <Box mt={3}>
                      <Typography variant="body1">from</Typography>
                      <Typography variant="h5">
                        <PriceFormat value={subscription.prices[0].price} />
                      </Typography>
                      <Typography variant="body1">
                        {paymentTypeTitle(subscription.prices[0].paymentType, false)}
                      </Typography>
                    </Box>
                  </Hidden>
                  <Hidden lgUp>
                    <Box mt={3}>
                      <Typography variant="body1">
                        <Box fontWeight="fontWeightBold" component="span">
                          from&nbsp;
                          <PriceFormat value={subscription.prices[0].price} />
                          &nbsp;{paymentTypeTitle(subscription.prices[0].paymentType, false)}
                        </Box>
                      </Typography>
                    </Box>
                    <Box mt={3} mb={3} mx="auto" className={classes.root} width="40%">
                      <QuoteButtonAddSubscription
                        quote={quote}
                        subscription={subscription}
                        onAddSubscription={onAddSubscription}
                        fullWidth
                      />
                    </Box>
                  </Hidden>
                </Box>
              </Grid>
              <Hidden mdDown>
                <Grid item xs={12}>
                  <Typography variant="h4">Your {subscription.name} package includes all the benefits of</Typography>
                  <Box mt={3}>{highlightSection()}</Box>
                </Grid>
              </Hidden>
              <Grid item xs={12}>
                <QuoteHr />
                <Box mt={isSmall ? 1 : 3} display="flex" alignItems="center">
                  <Box flexGrow={1} textAlign={{ xs: "center", md: "left" }}>
                    <Button onClick={onView} color="secondary">
                      View Details
                    </Button>
                  </Box>
                  <Hidden mdDown>
                    <Box className={classes.root} width="20%">
                      <QuoteButtonAddSubscription
                        quote={quote}
                        subscription={subscription}
                        onAddSubscription={onAddSubscription}
                        fullWidth
                      />
                    </Box>
                  </Hidden>
                </Box>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </Box>
    );
  };

  return <React.Fragment>{subscriptions!.map((li) => subscriptionView(li))}</React.Fragment>;
};

export default QuoteAddonWizardSubscriptions;
